import { DOCUMENT_TYPES } from '@/lib/documentTypes';

/**
 * Mi Boleta — validación del formulario de registro.
 *
 * Pieza **pura y sin React**, como el resto de las decisiones de esta carpeta:
 * qué campo está mal y por qué se verifica **sin montar interfaz**.
 *
 * **Los topes de largo no son inventados**: salen del contrato real de
 * `POST /auth/register` de api-core (medido sobre su OpenAPI el 2026-09-02):
 * `name` ≤ 200, `phone` ≤ 32. Validarlos acá evita que el fan llene todo el
 * formulario para recibir un `422` del servidor que no le dice nada.
 *
 * ⚠️ **La contraseña está acá aunque no la pidieron.** El requerimiento nombra
 * cuatro campos —nombre, correo, teléfono, nacimiento— pero
 * `POST /auth/register` la exige (`required: ['email','password']`): **sin
 * contraseña no se puede crear la cuenta**. Se incluye el mínimo indispensable
 * y nada más; queda señalado en el reporte para que sea una decisión del TL y
 * no un agregado silencioso.
 */

/** Topes tomados del contrato de api-core. */
export const NAME_MAX = 200;
export const PHONE_MAX = 32;
export const PASSWORD_MIN = 8;
export const DOCUMENT_TYPE_MAX = 8;
export const DOCUMENT_NUMBER_MAX = 64;

/** Mínimo de dígitos de un documento. Bajo a propósito: los pasaportes varían. */
const DOCUMENT_NUMBER_MIN = 5;

/** Edad máxima admitida: descarta tipeos como `1' + '899` sin poner un tope de edad real. */
const MAX_AGE_YEARS = 120;

export interface SignupFields {
  name: string;
  email: string;
  phone: string;
  /** `YYYY-MM-DD`, tal como lo entrega un `<input type="date">`. */
  birthDate: string;
  /**
   * Tipo de documento (`CC`, `CE`, `PA`, `NIT`) — ver `@/lib/documentTypes`.
   *
   * ⚠️ **Va junto al número y no es opcional.** El requerimiento habla de "el
   * documento" en singular, pero un número suelto **no identifica a nadie**: una
   * cédula y una cédula de extranjería pueden compartir dígitos, y el tipo es lo
   * que las distingue en la puerta del evento y en los soportes de la compra.
   * El backend también lo modela en dos campos.
   */
  documentType: string;
  documentNumber: string;
  password: string;
  /**
   * Repetición de la contraseña. Existe para atajar un error que el fan NO
   * puede detectar solo: la contraseña va enmascarada, así que un tipeo se
   * descubre recién al intentar ingresar —cuando ya no hay forma de saber qué
   * se escribió—. Compararla acá convierte una cuenta perdida en una corrección
   * de dos segundos.
   */
  passwordConfirm: string;
}

export type SignupFieldName = keyof SignupFields;
export type SignupErrors = Partial<Record<SignupFieldName, string>>;

/** Correo: chequeo deliberadamente laxo — validar de más rechaza direcciones válidas. */
function looksLikeEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

/** Teléfono: dígitos, con `+`, espacios, guiones y paréntesis tolerados. */
function looksLikePhone(value: string): boolean {
  const digits = value.replace(/[^\d]/g, '');
  return digits.length >= 7 && /^[\d\s+()-]+$/.test(value);
}

/**
 * Valida los campos y devuelve **sólo** los que están mal.
 *
 * `today` se inyecta para que la guarda de "fecha futura" no dependa del reloj
 * de la máquina que corre las pruebas.
 */
export function validateSignup(fields: SignupFields, today: Date = new Date()): SignupErrors {
  const errors: SignupErrors = {};
  const name = fields.name.trim();
  const email = fields.email.trim();
  const phone = fields.phone.trim();

  if (!name) errors.name = 'Escribí tu nombre.';
  else if (name.length > NAME_MAX)
    errors.name = `El nombre no puede pasar de ${NAME_MAX} caracteres.`;

  if (!email) errors.email = 'Escribí tu correo.';
  else if (!looksLikeEmail(email)) errors.email = 'Ese correo no parece válido.';

  if (!phone) errors.phone = 'Escribí tu teléfono.';
  else if (phone.length > PHONE_MAX)
    errors.phone = `El teléfono no puede pasar de ${PHONE_MAX} caracteres.`;
  else if (!looksLikePhone(phone)) errors.phone = 'Ese teléfono no parece válido.';

  if (!fields.birthDate) {
    errors.birthDate = 'Elegí tu fecha de nacimiento.';
  } else {
    const born = new Date(`${fields.birthDate}T00:00:00`);
    if (Number.isNaN(born.getTime())) {
      errors.birthDate = 'Esa fecha no es válida.';
    } else if (born > today) {
      // Nadie nació mañana. Sin esta guarda el dato entra y ensucia el perfil.
      errors.birthDate = 'La fecha de nacimiento no puede ser futura.';
    } else {
      const limit = new Date(today);
      limit.setFullYear(limit.getFullYear() - MAX_AGE_YEARS);
      if (born < limit) errors.birthDate = 'Revisá el año de nacimiento.';
    }
  }

  const documentNumber = fields.documentNumber.trim();

  if (!fields.documentType) {
    errors.documentType = 'Elegí el tipo de documento.';
  } else if (!DOCUMENT_TYPES.some((d) => d.value === fields.documentType)) {
    // Sólo puede pasar si alguien manipula el formulario: el selector no ofrece
    // otra cosa. Se valida igual — un tipo inventado llegaría al backend y de
    // ahí al soporte de una compra.
    errors.documentType = 'Ese tipo de documento no es válido.';
  } else if (fields.documentType.length > DOCUMENT_TYPE_MAX) {
    errors.documentType = 'Ese tipo de documento no es válido.';
  }

  if (!documentNumber) {
    errors.documentNumber = 'Escribí tu número de documento.';
  } else if (documentNumber.length > DOCUMENT_NUMBER_MAX) {
    errors.documentNumber = `El documento no puede pasar de ${DOCUMENT_NUMBER_MAX} caracteres.`;
  } else if (!/^[A-Za-z0-9.-]+$/.test(documentNumber)) {
    // Letras permitidas: los pasaportes y algunas cédulas de extranjería las
    // llevan. Rechazar sólo lo que claramente no es un documento.
    errors.documentNumber = 'Ese documento no parece válido.';
  } else if (documentNumber.replace(/[^A-Za-z0-9]/g, '').length < DOCUMENT_NUMBER_MIN) {
    errors.documentNumber = 'Ese documento parece incompleto.';
  }

  if (!fields.password) errors.password = 'Elegí una contraseña.';
  else if (fields.password.length < PASSWORD_MIN)
    errors.password = `La contraseña necesita al menos ${PASSWORD_MIN} caracteres.`;

  // La confirmación se compara SIEMPRE que haya algo escrito, aunque la
  // contraseña de arriba sea inválida: si sólo se avisara del largo, el fan
  // arregla eso, reenvía, y recién ahí descubre que además no coincidían.
  if (!fields.passwordConfirm) errors.passwordConfirm = 'Repetí la contraseña.';
  else if (fields.passwordConfirm !== fields.password)
    errors.passwordConfirm = 'Las contraseñas no coinciden.';

  return errors;
}

/** ¿Se puede enviar? */
export function isSignupValid(fields: SignupFields, today: Date = new Date()): boolean {
  return Object.keys(validateSignup(fields, today)).length === 0;
}

/** Formulario vacío — el estado inicial de la pantalla. */
export function emptySignupFields(): SignupFields {
  return {
    name: '',
    email: '',
    phone: '',
    birthDate: '',
    documentType: '',
    documentNumber: '',
    password: '',
    passwordConfirm: '',
  };
}

/**
 * Estado visible de la confirmación de contraseña, mientras se escribe.
 *
 * Es una decisión **de interfaz**, separada de `validateSignup`: aquélla corre
 * al enviar y sólo reporta lo que está mal; ésta corre en cada tecla y también
 * tiene algo bueno que decir. El aviso en verde de que **sí coinciden** es lo
 * que le confirma al fan que puede seguir, sin tener que enviar para saberlo.
 *
 * `'pendiente'` mientras falte escribir: no se le grita "no coinciden" a quien
 * apenas va por la primera letra de la repetición.
 */
export type PasswordMatchState = 'pendiente' | 'coinciden' | 'no-coinciden';

export function passwordMatchState(password: string, passwordConfirm: string): PasswordMatchState {
  if (!password || !passwordConfirm) return 'pendiente';
  return password === passwordConfirm ? 'coinciden' : 'no-coinciden';
}
